import { Outlet, UIMatch, useMatches } from 'react-router-dom';
import { SideDrawer } from '../SideDrawer/SideDrawer';
import { useEffect, useState, useRef } from 'react';
import { UserMenu } from '../UserMenu/UserMenu';
import { useAppSelector } from '@app/redux/hooks';
import DotsVerticalIcon from '@app/assets/icons/menu/dot-vertical.svg?react';
import UsageIcon from '@app/assets/icons/menu/usage.svg?react';
import MenuIcon from '@app/assets/icons/menu.svg?react';
import DiamondIcon from '@app/assets/icons/diamond.svg?react';
import clsx from 'clsx';
import { NavigationWrapper } from '../NavigationWrapper';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { PermissionProvider } from '@app/contexts/PermissionContext';
import { DateTime } from 'luxon';

type HandleType = {
  crumb: (param?: string) => React.ReactNode;
};

export const DashboardLayout = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [showUsageDropdown, setShowUsageDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const businessState = useAppSelector((state) => state.business);
  const authState = useAppSelector((state) => state.auth);
  const matches: UIMatch[] = useMatches();

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (!authState?.user?.subscriptions || !businessState?.selected?.id)
      return null;

    const businessSubscription = authState.user.subscriptions.find(
      (sub) => sub.businessId === businessState.selected?.id
    );

    if (
      !businessSubscription?.trialEnd ||
      businessSubscription.plan.type !== 'PROFESSIONAL'
    )
      return null;

    const currentTime = Math.floor(Date.now() / 1000);
    const trialEndTime = businessSubscription.trialEnd;
    const timeRemaining = trialEndTime - currentTime;
    const daysRemaining = Math.ceil(timeRemaining / (24 * 60 * 60));

    // Only show if trial is active (positive days remaining)
    return daysRemaining > 0 ? daysRemaining : null;
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  // Get business subscription
  const businessSubscription = authState?.user?.subscriptions?.find(
    (sub) => sub.businessId === businessState?.selected?.id
  );

  // Calculate subscription end date
  const getSubscriptionEndDate = () => {
    if (!businessSubscription) return null;

    const invoiceDate = businessSubscription?.trialEnd
      ? DateTime.fromSeconds(businessSubscription.trialEnd)
      : businessSubscription?.endDate
        ? DateTime.fromISO(businessSubscription.endDate)
        : null;

    if (!invoiceDate || !invoiceDate.isValid) return null;

    const day = invoiceDate.day;
    const suffix =
      day === 1 || day === 21 || day === 31
        ? 'st'
        : day === 2 || day === 22
          ? 'nd'
          : day === 3 || day === 23
            ? 'rd'
            : 'th';
    return `${day}${suffix} of ${invoiceDate.toFormat('MMMM, yyyy')}`;
  };

  const subscriptionEndDate = getSubscriptionEndDate();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUsageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const DashboardContent = () => {
    const { interceptNavigation } = useNavigationContext();

    let crumbs = matches
      .filter((match) =>
        Boolean(match.handle && (match.handle as HandleType).crumb)
      )
      .map((match) =>
        (match.handle as HandleType).crumb(match.data as string | undefined)
      );

    // Get current template name from localStorage for breadcrumb
    const getCurrentTemplateName = () => {
      try {
        const raw = localStorage.getItem('campaignFormData');
        if (raw) {
          const parsed = JSON.parse(raw);

          // First check for currentTemplateType flag (set by TemplateList)
          if (parsed.currentTemplateType) {
            switch (parsed.currentTemplateType) {
              case 'email':
                return 'Email design';
              case 'message':
                return 'Message design';
              case 'review':
                return 'Review form design';
              case 'feedback':
                return 'Feedback form design';
              case 'positive':
                return 'Positive window design';
              case 'negative':
                return 'Negative window design';
            }
          }
        }
      } catch {
        // ignore
      }
      return 'Default email template';
    };

    const handleMobileMenu = () => {
      setShowMenu(!showMenu);
    };


    return (
      <section className="flex min-h-screen">
        <div className="w-72 hidden md:block">
          <SideDrawer />
        </div>
        <div
          className={clsx('w-72 block md:hidden fixed h-full transform', {
            '-translate-x-72': !showMenu,
            'translate-x-0 z-[90]': showMenu,
          })}
        >
          <SideDrawer />
        </div>
        {showMenu && (
          <button
            onClick={() => handleMobileMenu()}
            className="fixed w-full inset-0 h-full bg-black/30 z-50"
          />
        )}
        <div className="w-full">
          <div className="block md:hidden bg-white text-sm border-b">
            <div className="container h-12 p-4 mx-auto flex items-center justify-between font-medium">
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => handleMobileMenu()}>
                  <MenuIcon />
                </button>
                <div
                  className="w-8 h-8 flex items-center justify-center font-bold text-lg rounded-lg"
                  style={{ color: `#424244`, background: `#42424414` }}
                >
                  {businessState?.selected?.name?.charAt(0)}
                </div>
                <h5 className="font-bold text-sm mr-auto">
                  {businessState?.selected?.name}
                </h5>
                <button onClick={() => interceptNavigation('/select-business')}>
                  <DotsVerticalIcon />
                </button>
              </div>
              <div>
                <UserMenu />
              </div>
            </div>
          </div>
          <div className="bg-white text-sm border-b hidden md:block template-editor-header">
            <div className="container h-12 p-4 mx-auto flex items-center justify-between font-medium">
              <h4>
                <span className="template-editor-title">
                  Campaigns → Create campaign →{' '}
                  <span id="template-editor-breadcrumb">
                    {getCurrentTemplateName()}
                  </span>
                </span>
                <span className="normal-title">{crumbs?.[0]}</span>
              </h4>
              <div className="flex gap-4 items-center template-editor-nav">
                {trialDaysRemaining && (
                  <div className="bg-[#F1F9FF] px-3 py-1.5 rounded-md flex items-center gap-2">
                    <span className="text-gray-800 text-sm">
                      Trial: {trialDaysRemaining} days left.
                    </span>
                    <button
                      onClick={() => interceptNavigation('/settings?tab=plan')}
                      className="text-[#3279C5] underline text-sm font-medium "
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() =>
                      interceptNavigation('/settings/plan/details')
                    }
                    className="items-center hidden md:flex"
                  >
                    <UsageIcon />
                    Usage and Plan
                  </button>

                  {/* Usage and Plan Dropdown */}
                  {showUsageDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-bold text-gray-800">
                            Usage & plan
                          </h3>
                          <span className="text-sm text-gray-500">
                            {subscriptionEndDate
                              ? `until ${subscriptionEndDate}`
                              : 'No subscription'}
                          </span>
                        </div>

                        <hr className="border-gray-200 mb-4" />

                        {/* Plan Type */}
                        <div className="w-full bg-gradient-to-r from-blue-400 to-green-400 rounded-lg p-3 text-white">
                          <div className="flex items-center gap-2">
                            <DiamondIcon className="w-5 h-5" />
                            <span className="font-bold text-lg">
                              {businessSubscription?.plan?.type === 'BASIC'
                                ? 'STARTER'
                                : businessSubscription?.plan?.type || 'NO PLAN'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* TODO: Enable notifications when ready
                <div className="hidden md:block">
                  {unreadNotifications === 0 ? (
                    <NotificationIcon width={18} height={19} />
                  ) : (
                    <NotificationUnreadIcon width={28} height={30} />
                  )}
                </div>
                */}
                <div>
                  <UserMenu />
                </div>
              </div>
            </div>
          </div>
          <div>
            <Outlet />
          </div>
        </div>
      </section>
    );
  };

  return (
    <PermissionProvider>
      <NavigationWrapper>
        <DashboardContent />
      </NavigationWrapper>
    </PermissionProvider>
  );
};
